import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Grain from "../components/Grain";
import { OfferDetails, offerPrice } from '../components/OfferDetails';
import { useOfferCatalog } from '../lib/useOfferCatalog';

export const SERVICES = [
  {
    key: "celebrations",
    titre: "Célébrations",
    n: "01",
    desc: "Mariages, baptêmes, anniversaires importants. Couverture de la cérémonie, des portraits, de la fête — approche documentaire avec moments posés.",
    img: "/images/generated/service_celebration.jpg",
    inclus: [
      "Reportage complet, ambiance et moments clés",
      "100 à 200 photos retouchées en HD",
      "Galerie privée en ligne",
      "Livraison sous 4 semaines, clé USB incluse",
    ],
    tarifs: "Essentiel CHF 1’590 · Signature CHF 2’400 · Prestige CHF 3’500",
  },
  {
    key: "maternite",
    titre: "Maternité & Bébé",
    n: "02",
    desc: "De la grossesse aux premiers mois du bébé. Séances en studio, lumière douce, ambiance intime, accompagnement bienveillant.",
    img: "/images/generated/service_grossesse.jpg",
    inclus: [
      "Grossesse : 1 h · 15 photos retouchées",
      "Nouveau-né : 2 à 3 h · 20 photos retouchées",
      "Galerie en ligne privée",
      "Livraison de la galerie : 2 à 3 jours",
    ],
    tarifs: "Dès CHF 350 (grossesse) · Dès CHF 550 (nouveau-né)",
  },
  {
    key: "portrait",
    titre: "Portrait & Branding",
    n: "03",
    desc: "Portraits professionnels pour LinkedIn, personal branding pour entrepreneurs, headshots premium. Éclairage studio maîtrisé.",
    img: "/images/generated/service_portrait.jpg",
    inclus: [
      "1 h · 2 à 3 tenues · 15 photos retouchées",
      "Conseils de pose et de style",
      "2 fonds au choix",
      "Livraison de la galerie : 2 à 3 jours",
    ],
    tarifs: "Dès CHF 350",
  },
  {
    key: "studio",
    titre: "Book & Headshot studio",
    n: "04",
    desc: "Séances studio avec photographe pour besoins spécifiques (book mannequin, e-commerce, ambiance créative). Matériel pro fourni.",
    img: "/images/generated/service_studio.jpg",
    inclus: [
      "Studio équipé 45 m²",
      "Matériel d'éclairage professionnel",
      "Accompagnement photographique",
    ],
    tarifs: "Sur devis",
  },
];

export default function Services() {
  const catalog = useOfferCatalog();
  return (
    <div className="pt-28">
      <section className="section-dark py-20 md:py-28 px-6 md:px-12 relative overflow-hidden" data-testid="services-hero">
        <Grain />
        <div className="absolute -top-20 left-1/3 w-[600px] h-[600px] glow-amber-soft pointer-events-none" />
        <div className="max-w-[1280px] mx-auto relative z-10">
          <div className="eyebrow mb-6">Services</div>
          <h1 className="h-display text-6xl md:text-8xl">
            Quatre univers,<br /><span className="italic-accent">une signature.</span>
          </h1>
        </div>
      </section>

      {SERVICES.map((s, i) => {
        const isDark = i % 2 === 1;
        const offerSlugs = {maternite:['grossesse-classique','nouveau-ne'],portrait:['portrait-essentiel']}[s.key] || [];
        const offers = catalog.filter(offer => offerSlugs.includes(offer.slug));
        return (
          <section
            key={s.key}
            data-testid={`service-${s.key}`}
            className={`${isDark ? "section-dark" : "section-cream"} py-24 md:py-32 px-6 md:px-12`}
          >
            <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-10 items-center">
              <div className={`md:col-span-6 ${i % 2 === 0 ? "" : "md:order-2"}`}>
                <div className="aspect-[4/5] overflow-hidden hover-zoom">
                  <img src={s.img} alt={s.titre} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className={`md:col-span-6 ${i % 2 === 0 ? "" : "md:order-1"}`}>
                <div className={`font-serif italic text-7xl ${isDark ? "text-accent-light" : "text-accent-deep"}`}>{s.n}</div>
                <h2 className="font-serif text-5xl md:text-6xl font-light mt-2">{s.titre}</h2>
                <p className={`mt-6 font-light text-lg leading-relaxed ${isDark ? "text-text-cream/70" : "text-text-dark/70"}`}>
                  {s.desc}
                </p>
                {offers.length ? <div className="mt-8 space-y-8">{offers.map(offer => <div key={offer.slug} data-testid={`services-offer-${offer.slug}`}>
                  <h3 className="font-serif text-xl mb-4" data-testid={`services-price-${offer.slug}`}>{offer.name} · {offerPrice(offer)}</h3>
                  <OfferDetails offer={offer} id={`services-${offer.slug}`}/>
                </div>)}</div> : <ul className="mt-8 space-y-3">
                  {s.inclus.map((it) => (
                    <li key={it} className="flex items-start gap-3">
                      <Check size={16} className="mt-1 text-accent-deep flex-shrink-0" strokeWidth={1.5}/>
                      <span className={`font-light ${isDark ? "text-text-cream/80" : "text-text-dark/80"}`}>{it}</span>
                    </li>
                  ))}
                </ul>}
                <div className={`mt-8 pt-6 border-t ${isDark ? "border-white/10" : "border-text-dark/10"}`}>
                  <div className="eyebrow mb-2 text-accent-deep">Tarifs</div>
                  <div className={`font-serif text-xl ${isDark ? "text-text-cream" : "text-text-dark"}`}>{s.tarifs}</div>
                </div>
                <div className="mt-8">
                  <Link to={`/univers/${s.key}`} className={isDark ? "btn-primary" : "btn-dark"} data-testid={`reserve-${s.key}`}>
                    Réserver une séance <ArrowRight size={14} strokeWidth={1.5}/>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="section-dark py-24 px-6 md:px-12 text-center" data-testid="services-cta">
        <Link to="/contact" className="btn-secondary">Une question ? Me contacter</Link>
      </section>
    </div>
  );
}
